/** @jsxImportSource react */
import clsxm from '@common/lib/clsxm';
import PricingTrendForm from '@react/widgets/pricingTrend/PricingTrendForm';
import { useRouter } from 'next/router';

import { useDatahub } from '../hooks/useDatahub';

export default function HeaderSection() {
  const router = useRouter();
  const { query } = router;
  const { searchInsights, locationStringWithCountryCode } = useDatahub();

  const { toc, state, city, roomType } = query;

  const handleSubmit = (values: any) => {
    const currentFilters = {
      state: '',
      city: '',
      toc: '',
      roomType: '',
    };
    const searchParams = values?.location?.searchParams;

    if (searchParams?.state) {
      currentFilters.state = searchParams.state;
    }
    if (searchParams?.city) {
      currentFilters.city = searchParams.city;
    }
    currentFilters.toc = values?.toc;

    currentFilters.roomType = values?.typeOfRoom;

    if (!currentFilters.toc || !currentFilters.city || !currentFilters.state) {
      return;
    }

    router.push(
      {
        pathname: '/senior-living-pricing-trends',
        query: currentFilters,
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <>
      <div
        className={clsxm(
          'mb-xxl flex items-center justify-center bg-viridian-lighter-90 px-m pt-xxl'
        )}
      >
        <section className='mx-auto w-full max-w-4xl space-y-4'>
          <div className='flex flex-col-reverse items-center justify-center  md:flex-row md:justify-between'>
            <div>
              <h1 className='font-t-xxl mb-4'>Senior Living Pricing Trends</h1>
              <p>
                Senior living costs will vary based on room type and the level
                of care needed. Use Seniorly&apos;s pricing tools to calculate
                and better understand senior living costs in your area.
              </p>
            </div>
            <div className='relative text-white'>
              <svg
                width='328'
                height='272'
                viewBox='0 0 328 272'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle cx='164' cy='140' r='80' fill='#D1E3E3' />
                <rect
                  x='188'
                  y='152'
                  width='88'
                  height='88'
                  rx='16'
                  fill='#C5B497'
                />
                <path
                  d='M233 198H230C229.717 198 229.479 198.096 229.288 198.288C229.096 198.479 229 198.717 229 199C229 199.283 229.096 199.521 229.288 199.713C229.479 199.904 229.717 200 230 200H231C231 200.283 231.096 200.521 231.288 200.713C231.479 200.904 231.716 201 231.997 201C232.278 201 232.505 200.898 232.677 200.695C232.849 200.491 232.956 200.259 233 200H234C234.283 200 234.521 199.904 234.713 199.713C234.904 199.521 235 199.283 235 199V196C235 195.717 234.904 195.479 234.713 195.288C234.521 195.096 234.283 195 234 195H231V194H234C234.283 194 234.521 193.904 234.713 193.713C234.904 193.521 235 193.283 235 193C235 192.717 234.904 192.479 234.713 192.288C234.521 192.096 234.283 192 234 192H233C233 191.717 232.904 191.479 232.713 191.288C232.521 191.096 232.284 191 232.003 191C231.722 191 231.495 191.102 231.324 191.305C231.152 191.509 231.044 191.741 231 192H230C229.717 192 229.479 192.096 229.288 192.288C229.096 192.479 229 192.717 229 193V196C229 196.283 229.096 196.521 229.288 196.713C229.479 196.904 229.717 197 230 197H233V198ZM224.072 204.203C223.442 204.203 222.906 203.982 222.462 203.538C222.019 203.095 221.797 202.558 221.797 201.928V190.072C221.797 189.442 222.019 188.905 222.462 188.462C222.906 188.018 223.442 187.797 224.072 187.797H239.928C240.558 187.797 241.095 188.018 241.538 188.462C241.982 188.905 242.203 189.442 242.203 190.072V201.928C242.203 202.558 241.982 203.095 241.538 203.538C241.095 203.982 240.558 204.203 239.928 204.203H224.072ZM224.072 201.928H239.928V190.072H224.072V201.928Z'
                  fill='white'
                />
                <rect
                  x='52'
                  y='152'
                  width='88'
                  height='88'
                  rx='16'
                  fill='#339F43'
                />
                <path
                  d='M86.5982 201.751C86.3703 201.527 86.2595 201.258 86.2659 200.945C86.2722 200.633 86.381 200.368 86.5923 200.153L91.8134 194.875C92.2526 194.436 92.7913 194.216 93.4298 194.216C94.0682 194.216 94.6069 194.436 95.0461 194.875L97.4298 197.278L102.331 192.415H100.934C100.615 192.415 100.345 192.305 100.126 192.085C99.9064 191.866 99.7966 191.596 99.7966 191.277C99.7966 190.958 99.9064 190.689 100.126 190.469C100.345 190.249 100.615 190.14 100.934 190.14H105.066C105.385 190.14 105.654 190.249 105.874 190.469C106.093 190.689 106.203 190.958 106.203 191.277V195.403C106.203 195.722 106.093 195.991 105.874 196.211C105.654 196.43 105.385 196.54 105.066 196.54C104.746 196.54 104.477 196.43 104.257 196.211C104.038 195.991 103.928 195.722 103.928 195.403V194.006L99.0461 198.888C98.6069 199.327 98.0682 199.547 97.4298 199.547C96.7913 199.547 96.2526 199.327 95.8134 198.888L93.4298 196.51L88.1895 201.751C87.9783 201.962 87.7131 202.067 87.3939 202.067C87.0747 202.067 86.8095 201.962 86.5982 201.751Z'
                  fill='white'
                />
                <rect
                  x='120'
                  y='32'
                  width='88'
                  height='88'
                  rx='16'
                  fill='#74A7DC'
                />
                <path
                  d='M158.072 82.9283H160.863V78C160.863 77.6808 160.972 77.4114 161.192 77.1919C161.412 76.9723 161.681 76.8625 162 76.8625H166C166.319 76.8625 166.589 76.9723 166.808 77.1919C167.028 77.4114 167.138 77.6808 167.138 78V82.9283H169.928V74.0359L164 69.5897L158.072 74.0359V82.9283ZM155.797 82.9283V74.0359C155.797 73.6763 155.877 73.3357 156.036 73.0141C156.196 72.6924 156.419 72.4259 156.704 72.2147L162.633 67.7685C163.035 67.462 163.49 67.3087 164 67.3087C164.51 67.3087 164.966 67.462 165.368 67.7685L171.296 72.2147C171.582 72.4259 171.804 72.6924 171.964 73.0141C172.124 73.3357 172.203 73.6763 172.203 74.0359V82.9283C172.203 83.558 171.982 84.0946 171.538 84.5381C171.095 84.9815 170.558 85.2033 169.928 85.2033H166.09C165.771 85.2033 165.501 85.0935 165.282 84.8739C165.062 84.6544 164.952 84.385 164.952 84.0658V79.0478H163.048V84.0658C163.048 84.385 162.938 84.6544 162.719 84.8739C162.499 85.0935 162.23 85.2033 161.91 85.2033H158.072C157.442 85.2033 156.906 84.9815 156.462 84.5381C156.019 84.0946 155.797 83.558 155.797 82.9283Z'
                  fill='white'
                />
              </svg>
            </div>
          </div>

          <div className='!-mb-32 rounded border border-slate-lighter-90 bg-white p-8 shadow-lg'>
            <PricingTrendForm
              initialFilters={{
                toc,
                typeOfRoom: roomType,
                location: {
                  displayText: locationStringWithCountryCode,
                  searchParams: {
                    state,
                    city,
                  },
                },
              }}
              handleSubmit={handleSubmit}
              locationString={locationStringWithCountryCode}
              nearest={searchInsights?.nearest}
            />
          </div>
        </section>
      </div>
      <div className='h-32' />
    </>
  );
}
